/**
 * Loading / empty / error / not-yet-available states.
 *
 * These are not edge cases here. On day one the database is empty and most of
 * the analytical routes have nothing to compute from, so "no data yet" is the
 * ordinary state and has to read as a normal, explained condition rather than
 * as a broken screen. Equally important: an empty state must never be filled in
 * with a plausible-looking placeholder number.
 */

import type { ReactNode } from 'react';
import { ApiError } from '../../lib/api';
import { useT } from '../../i18n';
import type { Dictionary } from '../../i18n';

function Skeleton({ height = 16, width }: { height?: number; width?: number | string }) {
  return <div className="skeleton" style={{ height, width: width ?? '100%' }} />;
}

export function LoadingBlock({ label, rows = 3 }: { label?: string; rows?: number }) {
  const t = useT();
  return (
    <div className="stack" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label ?? t.states.loading}</span>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} height={index === 0 ? 28 : 16} width={index === 0 ? '40%' : '100%'} />
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="state-block">
      <div className="state-block__title">{title}</div>
      <div className="state-block__body">{body}</div>
      {action}
    </div>
  );
}

/**
 * A screen whose backing route is not built yet.
 *
 * Distinct from `EmptyState` on purpose: "nobody has recorded a Bear session"
 * and "this feature has no server behind it" are different facts, and an
 * operator planning around the tool deserves to know which one they are
 * looking at.
 */
export function NotAvailableYet({
  feature,
  phase,
  describes,
}: {
  feature: string;
  /** The spec phase that delivers it, so the wait is bounded and legible. */
  phase: string;
  /** What it will show once it exists. */
  describes: ReactNode;
}) {
  const t = useT();
  return (
    <div className="state-block">
      <div className="state-block__title">{t.states.notAvailableYetTitle(feature)}</div>
      <div className="state-block__body">
        <p style={{ marginTop: 0 }}>{describes}</p>
        <p style={{ marginBottom: 0 }}>{t.states.notAvailableYetBody(phase)}</p>
      </div>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const api = error instanceof ApiError ? error : null;

  if (api?.isMissingRoute) {
    return (
      <div className="state-block">
        <div className="state-block__title">{t.states.missingRouteTitle}</div>
        <div className="state-block__body">{t.states.missingRouteBody}</div>
      </div>
    );
  }

  return (
    <div className="state-block state-block--error" role="alert">
      <div className="state-block__title">
        {api ? errorTitle(t, api.kind) : t.states.genericErrorTitle}
      </div>
      <div className="state-block__body">
        {error instanceof Error ? error.message : t.states.unexpectedError}
      </div>
      {api?.detail ? <div className="state-block__detail">{api.detail}</div> : null}
      {onRetry ? (
        <button type="button" className="btn btn--sm" onClick={onRetry}>
          {t.common.tryAgain}
        </button>
      ) : null}
    </div>
  );
}

/**
 * `kind` is a union with a `string` arm — the Worker can name a failure this
 * app has not learned yet — so an unrecognised kind falls through to the
 * generic title rather than rendering a raw enum at the operator.
 */
function errorTitle(t: Dictionary, kind: string): string {
  const titles = t.states.errorTitles;
  switch (kind) {
    case 'UNAUTHENTICATED':
      return titles.UNAUTHENTICATED;
    case 'FORBIDDEN':
      return titles.FORBIDDEN;
    case 'RATE_LIMIT':
      return titles.RATE_LIMIT;
    case 'CONTRACT_MISMATCH':
      return titles.CONTRACT_MISMATCH;
    case 'NETWORK':
      return titles.NETWORK;
    default:
      return titles.REQUEST_FAILED;
  }
}

/**
 * The standard query-state switch.
 *
 * Every screen routes through this so that loading, error and empty are handled
 * identically everywhere and cannot be quietly skipped.
 */
export function QueryBoundary<T>({
  query: result,
  loadingLabel,
  isEmpty,
  empty,
  children,
}: {
  query: {
    data: T | undefined;
    isPending: boolean;
    error: unknown;
    refetch?: () => unknown;
  };
  loadingLabel?: string;
  isEmpty?: (data: T) => boolean;
  empty?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  const t = useT();
  if (result.isPending) return <LoadingBlock label={loadingLabel ?? t.states.loading} />;
  if (result.error) {
    return (
      <ErrorState
        error={result.error}
        onRetry={result.refetch ? () => void result.refetch?.() : undefined}
      />
    );
  }
  if (result.data === undefined) {
    return <EmptyState title={t.states.noDataTitle} body={t.states.noDataBody} />;
  }
  if (isEmpty?.(result.data) && empty) return <>{empty}</>;
  return <>{children(result.data)}</>;
}
